export type DeleteAccountResult = { ok: true } | { ok: false; message: string };

const TOO_MANY = 'Bạn thao tác quá nhanh, vui lòng thử lại sau một phút.';
const OFFLINE = 'Không kết nối được máy chủ, vui lòng thử lại sau.';
const BAD_CREDENTIALS = 'Tài khoản hoặc mật khẩu không đúng.';
const GENERIC = 'Không xoá được tài khoản, vui lòng thử lại.';

// REACT_APP_API_URL đã bao gồm '/api/', ví dụ https://api.ebdlearning.vn/api/
function apiUrl(path: string): string {
  const base = process.env.REACT_APP_API_URL || 'http://localhost:8080/api/';
  return `${base.replace(/\/+$/, '')}/${path}`;
}

/**
 * Dịch một phản hồi thất bại thành thông điệp tiếng Việt, trả về null nếu phản
 * hồi thành công và người gọi đi tiếp được.
 *
 * Gom ở đây để thêm một mã trạng thái mới chỉ phải sửa một chỗ — khối này trước
 * đây bị lặp nguyên si ở cả bước đăng nhập lẫn bước xoá.
 *
 * `clientErrorMessage` là phần khác nhau giữa hai bước: đăng nhập hỏng nghĩa là
 * sai thông tin đăng nhập, còn xoá hỏng với mã 400 thì dùng thông điệp do server
 * trả về.
 */
async function triageFailure(
  response: Response,
  clientErrorMessage: (response: Response) => string | Promise<string>
): Promise<DeleteAccountResult | null> {
  if (response.status === 429) {
    return { ok: false, message: TOO_MANY };
  }
  if (response.status >= 500) {
    return { ok: false, message: OFFLINE };
  }
  if (response.ok) {
    return null;
  }
  return { ok: false, message: await clientErrorMessage(response) };
}

async function readMessage(response: Response, fallback: string): Promise<string> {
  try {
    const body = await response.json();
    if (body && typeof body.message === 'string' && body.message.trim()) {
      return body.message;
    }
  } catch {
    // Thân phản hồi không phải JSON — dùng thông điệp mặc định
  }
  return fallback;
}

export async function deleteAccount(
  userName: string,
  password: string
): Promise<DeleteAccountResult> {
  try {
    const loginResponse = await fetch(apiUrl('auth/login'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_name: userName, password }),
    });

    const loginFailure = await triageFailure(loginResponse, () => BAD_CREDENTIALS);
    if (loginFailure) {
      return loginFailure;
    }

    let token: string | undefined;
    try {
      const body = await loginResponse.json();
      token = body?.token;
    } catch {
      return { ok: false, message: GENERIC };
    }
    if (!token) {
      return { ok: false, message: GENERIC };
    }

    const deleteResponse = await fetch(apiUrl('auth/delete-account'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ password }),
    });

    const deleteFailure = await triageFailure(deleteResponse, (response) =>
      response.status === 400 ? readMessage(response, GENERIC) : GENERIC
    );
    if (deleteFailure) {
      return deleteFailure;
    }

    return { ok: true };
  } catch {
    return { ok: false, message: OFFLINE };
  }
}
